import { onSnapshot, type Unsubscribe } from 'firebase/firestore';
import type { ChatMessage } from '../components/GlobalChatSystem/types';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import {
  Paths,
  assertCurrentUid,
  debouncedSetDoc,
  getDoc,
  withBackoff,
} from './firestoreUtils';
import {
  ChatThreadMessagesDoc,
  MAX_THREAD_MESSAGES,
  fromChatThreadMessagesDoc,
  toChatThreadMessagesDoc,
} from '../types/chatThreadMessagesFirestore';
import { mergeLegacyAndPerDoc } from '../types/chatThreadMessageDocFirestore';
import { collapseUserRowTwins } from '../services/groupThreads/userRowTwins';
import {
  getGroupThreadOverlay,
  hasAnyGroupThreadMessages,
  mergeOwnAndGroupMessages,
} from './groupThreadMessagesRepository';
import {
  getNewestThreadMessageDocsPage,
  getOlderThreadMessageDocsPage,
  getThreadMessageDocs,
  hasThreadMessageDocs,
  subscribeNewestThreadMessageDocs,
  upsertThreadMessageDocs,
  THREAD_MESSAGES_PAGE_SIZE,
  type ThreadMessageDocsPageCursor,
} from './chatThreadMessageDocsRepository';

const THREAD_MESSAGES_DOMAIN = 'chat_thread_messages';
const WRITE_DEBOUNCE_MS = 150;

const pendingWritesByUid: Map<string, Promise<void>> = new Map();

function enqueueUserWrite(uid: string, work: () => Promise<void>): Promise<void> {
  const previous = pendingWritesByUid.get(uid) || Promise.resolve();
  const next = previous.catch(() => {}).then(work);
  pendingWritesByUid.set(uid, next);
  next.finally(() => {
    if (pendingWritesByUid.get(uid) === next) {
      pendingWritesByUid.delete(uid);
    }
  }).catch(() => {});
  return next;
}

function threadMessagesRef(uid: string, threadId: string) {
  return Paths.domainDoc(uid, THREAD_MESSAGES_DOMAIN, threadId);
}

export async function upsertThreadMessages(
  uid: string,
  instanceId: string,
  threadId: string,
  messages: ChatMessage[],
): Promise<void> {
  addUserScopedWriteGuard(uid, 'upsertThreadMessages');
  assertCurrentUid(uid, 'upsertThreadMessages');
  const ref = threadMessagesRef(uid, threadId);
  const data = toChatThreadMessagesDoc(threadId, instanceId, messages);

  await enqueueUserWrite(uid, async () => {
    try {
      await withBackoff(() => debouncedSetDoc(ref, data, {
        debounceKey: ref.path,
        debounceMs: WRITE_DEBOUNCE_MS,
      }));
    } catch (err) {
      // NEVER silent (2026-08-20 attachment-durability round): a failed
      // whole-array upsert means the thread's server transcript stops
      // advancing — the "messages/attachments disappeared after reload"
      // class. The size guard in toChatThreadMessagesDoc prevents the known
      // oversize cause; anything that still fails must be loudly visible.
      // eslint-disable-next-line no-console
      console.error('[chatThreadMessages] thread transcript upsert FAILED — server copy is now stale', {
        threadId,
        path: ref.path,
        messageCount: messages.length,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  });

  // Phase 3 dual-write: additively persist per-message docs. Best-effort — the
  // legacy array doc above remains authoritative, so a per-doc failure never
  // loses a message. Fire-and-forget so the mirror effect stays snappy.
  void upsertThreadMessageDocs(uid, instanceId, threadId, messages).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('[chatThreadMessages] per-message dual-write failed (legacy doc unaffected)', err);
  });
}

// Phase 3 READ-CUTOVER gate. ENABLED (2026-07-12) after the confirmed migration
// hazards were closed at their roots and covered by tests:
//   • Finding 2 (DUPLICATE report) — the client bubble, the client hydrate
//     recovery, and the server completion write now converge on ONE per-message
//     doc via the durable `taskAnchorId` (`prest0n_task_<taskId>`), frozen from
//     serverTaskId and never cleared. mergeLegacyAndPerDoc keys on that anchor
//     (with a legacy-bridge alias for pre-fix orphan docs), so the union yields
//     a single answer. See perDocConvergence.test.ts + taskAnswerMergeConvergence.
//   • Finding 4 (unguarded merge:true DOWNGRADE) — the writer now persists only
//     SETTLED messages (isSettledForPerDoc), so no streaming/interrupted copy can
//     overwrite a finalized doc; the read-merge keeps the more-complete copy.
//   • Finding 1 (id-less COLLAPSE) — stableMessageId hashes every distinguishing
//     field, and empty-body tiles are never written, so a collapse can happen
//     only between messages identical in all observable content (a true dup the
//     reducer already merges).
// The legacy whole-array doc is STILL written and never deleted, so it remains a
// complete fallback: the merge is additive-safe (union, never-downgrade), not a
// destructive cutover. If a regression is ever suspected, flip back to false for
// an instant, zero-loss revert to legacy-authoritative reads.
const PER_MESSAGE_DOC_READ_ENABLED = true;

export async function getThreadMessages(uid: string, threadId: string): Promise<ChatMessage[] | null> {
  const ownRaw = await getOwnThreadMessages(uid, threadId);
  // PR-F0 (2026-09-21, userRowTwins.ts): the owner tree can hold one question
  // twice — the client bubble and the shared store's twin of it. Collapse on the
  // id link BEFORE the overlay merge below keys the own list, so the twin never
  // wins a collision against its own echo and never renders a second time.
  const own = ownRaw ? collapseUserRowTwins(ownRaw) : ownRaw;
  // ── P3c group overlay (flag-gated, fail-closed). Under Option B neither
  // store is complete alone: the owner's tree lacks OTHER participants' human
  // turns, and a non-owner's tree starts empty. getGroupThreadOverlay returns
  // null while the flag is off / read is rules-denied, keeping this exit
  // byte-identical to the pre-P3c behavior.
  const overlay = await getGroupThreadOverlay(threadId);
  if (overlay && overlay.length > 0) {
    return mergeOwnAndGroupMessages(own ?? [], overlay, uid);
  }
  return own;
}

async function getOwnThreadMessages(uid: string, threadId: string): Promise<ChatMessage[] | null> {
  assertCurrentUid(uid, 'getThreadMessages');
  const ref = threadMessagesRef(uid, threadId);
  const legacySnap = await withBackoff(() => getDoc(ref));
  const legacyData = legacySnap.exists() ? (legacySnap.data() as ChatThreadMessagesDoc) : null;
  const legacyMessages = legacyData ? fromChatThreadMessagesDoc(legacyData) : [];

  if (!PER_MESSAGE_DOC_READ_ENABLED) {
    // LEGACY-AUTHORITATIVE (safe default). Return the legacy array verbatim when
    // it exists — identical to pre-Phase-3. Per-docs are written but not read,
    // so none of the confirmed merge hazards can surface in production. The
    // per-doc store still populates via dual-write for the eventual cutover.
    if (legacyMessages.length > 0) return legacyMessages;
    // Legacy absent → fall back to per-docs (a thread with no legacy doc, e.g.
    // fully post-cutover). Safe: no cross-store merge involved.
    const perDocOnly = await getThreadMessageDocs(uid, threadId).catch(() => [] as ChatMessage[]);
    if (perDocOnly.length > 0) return perDocOnly;
    return legacySnap.exists() ? [] : null;
  }

  // ── GATED read-cutover (dual-read merge). Not reached while the flag is off. ──
  const perDoc = await getThreadMessageDocs(uid, threadId).catch(() => [] as ChatMessage[]);
  if (legacyMessages.length === 0 && perDoc.length === 0) {
    return legacySnap.exists() ? [] : null;
  }
  if (perDoc.length === 0 && legacyMessages.length > 0) {
    const instanceId = typeof legacyData?.instanceId === 'string' ? legacyData.instanceId : '';
    void upsertThreadMessageDocs(uid, instanceId, threadId, legacyMessages).catch(() => { /* best-effort backfill */ });
  }
  return mergeLegacyAndPerDoc(legacyMessages, perDoc);
}

// ── I4 (2026-07-15): paged thread-open reads ─────────────────────────────────

// Re-export so hook-level callers can hold the opaque cursor without importing
// the per-doc repository directly.
export type { ThreadMessageDocsPageCursor };

export interface ThreadMessagesPageResult {
  /** The loaded window, chronological. */
  messages: ChatMessage[];
  /** True when OLDER per-doc pages exist beyond this window. */
  olderAvailable: boolean;
  /** Cursor for `getOlderThreadMessages`, or null when nothing older exists. */
  cursor: ThreadMessageDocsPageCursor | null;
  /**
   * 'paged'       — per-doc store non-empty: newest per-doc page merged with
   *                 the (whole, capped) legacy array doc.
   * 'legacy-full' — per-doc store empty (pre-migration thread): the legacy
   *                 array is returned verbatim, exactly like getThreadMessages,
   *                 and the best-effort backfill is kicked so the NEXT open can
   *                 page.
   */
  mode: 'paged' | 'legacy-full';
}

/**
 * Paged thread-open read (I4 — "Load earlier messages"). Bounds the first
 * server load of a long thread to the newest window instead of the full
 * uncapped per-doc history.
 *
 * Paged-mode window shape: the legacy whole-array doc is a single document
 * holding the newest ≤MAX_THREAD_MESSAGES (100) messages (a capped SUFFIX of
 * history), and the newest per-doc page is also a suffix — so their
 * `mergeLegacyAndPerDoc` union is one CONTIGUOUS newest window (never a gapped
 * transcript). All Phase-3 convergence invariants (anchor-keyed union,
 * never-downgrade, legacy alias bridge) apply to the window unchanged.
 *
 * Legacy fallback condition: the per-doc store being EMPTY is the exact
 * pre-migration signal today's `getThreadMessages` uses to return the legacy
 * array and schedule the backfill — a pre-migration thread keeps its full-load
 * behavior (`mode: 'legacy-full'`, nothing older to page).
 *
 * Returns null only when NEITHER store has the thread (same contract as
 * getThreadMessages), so callers that treat null as "thread unknown to the
 * server" keep working.
 */
export async function getThreadMessagesPage(
  uid: string,
  threadId: string,
  opts?: { pageSize?: number },
): Promise<ThreadMessagesPageResult | null> {
  const own = await getOwnThreadMessagesPage(uid, threadId, opts);
  // ── P3c group overlay (flag-gated, fail-closed; see getThreadMessages). The
  // overlay is deliberately NOT paged for now: it is bounded by the migrated
  // legacy window (~100) plus live group turns, and paging it would need a
  // cross-store cursor. Merged into whichever window the own read produced.
  const overlay = await getGroupThreadOverlay(threadId);
  if (overlay && overlay.length > 0) {
    const base = own ?? { messages: [], olderAvailable: false, cursor: null, mode: 'legacy-full' as const };
    return { ...base, messages: mergeOwnAndGroupMessages(base.messages, overlay, uid) };
  }
  return own;
}

/**
 * The thread-OPEN per-doc window (P0 thread-durability, 2026-09-02): the
 * legacy whole-array doc holds the newest MAX_THREAD_MESSAGES (100) and its
 * card-html backstop blanks the OLDEST of them first. A 60-message newest
 * per-doc page left legacy positions 61–100 — exactly the ones the backstop
 * targets — with no intact per-doc partner in the merge, so a stripped
 * legacy-only copy was the only copy shown. Opening on the legacy window
 * closes that asymmetry: every legacy-window message meets its per-doc
 * partner, and the merge (which now scores card html as body) keeps the
 * intact one. Older pages keep walking THREAD_MESSAGES_PAGE_SIZE (60); an
 * explicit `opts.pageSize` is honored verbatim (repository tests drive the
 * paging engine with small pages).
 */
export const THREAD_OPEN_WINDOW_SIZE = Math.max(Number(THREAD_MESSAGES_PAGE_SIZE) || 0, MAX_THREAD_MESSAGES);

async function getOwnThreadMessagesPage(
  uid: string,
  threadId: string,
  opts?: { pageSize?: number },
): Promise<ThreadMessagesPageResult | null> {
  assertCurrentUid(uid, 'getThreadMessagesPage');
  const pageSize = opts?.pageSize ?? THREAD_OPEN_WINDOW_SIZE;
  const ref = threadMessagesRef(uid, threadId);
  const legacySnap = await withBackoff(() => getDoc(ref));
  const legacyData = legacySnap.exists() ? (legacySnap.data() as ChatThreadMessagesDoc) : null;
  const legacyMessages = legacyData ? fromChatThreadMessagesDoc(legacyData) : [];

  const page = await getNewestThreadMessageDocsPage(uid, threadId, pageSize)
    .catch(() => null);

  if (!page || page.messages.length === 0) {
    // Pre-migration (or per-doc-unreadable) thread: unchanged full-load
    // behavior, including the best-effort backfill getThreadMessages runs.
    if (legacyMessages.length > 0) {
      const instanceId = typeof legacyData?.instanceId === 'string' ? legacyData.instanceId : '';
      void upsertThreadMessageDocs(uid, instanceId, threadId, legacyMessages)
        .catch(() => { /* best-effort backfill */ });
      return { messages: legacyMessages, olderAvailable: false, cursor: null, mode: 'legacy-full' };
    }
    if (!legacySnap.exists()) return null;
    return { messages: [], olderAvailable: false, cursor: null, mode: 'legacy-full' };
  }

  return {
    messages: collapseUserRowTwins(mergeLegacyAndPerDoc(legacyMessages, page.messages)),
    olderAvailable: page.cursor !== null,
    cursor: page.cursor,
    mode: 'paged',
  };
}

/**
 * Fetch the next OLDER page for a thread previously opened via
 * `getThreadMessagesPage` (per-doc walk; the legacy doc was already consumed
 * whole by the initial read). The caller merges the result into its transcript
 * with `mergeLegacyAndPerDoc(current, page.messages)` so anchor-keyed copies
 * across the page boundary converge instead of duplicating.
 */
export async function getOlderThreadMessages(
  uid: string,
  threadId: string,
  cursor: ThreadMessageDocsPageCursor,
  opts?: { pageSize?: number },
): Promise<{ messages: ChatMessage[]; olderAvailable: boolean; cursor: ThreadMessageDocsPageCursor | null }> {
  assertCurrentUid(uid, 'getOlderThreadMessages');
  const pageSize = opts?.pageSize ?? THREAD_MESSAGES_PAGE_SIZE;
  const page = await getOlderThreadMessageDocsPage(uid, threadId, cursor, pageSize);
  return {
    messages: page.messages,
    olderAvailable: page.cursor !== null,
    cursor: page.cursor,
  };
}

/**
 * Cheap server-emptiness probe (I4): does this thread have ANY message on the
 * server, in either store? One legacy doc read + one limit(1) per-doc query —
 * never a paged/full transcript read. Greet-on-focus MUST use this instead of
 * interpreting a paged read: a paged result being empty proves per-doc
 * emptiness only for the newest window, and a full read bills O(history) for
 * a boolean.
 */
export async function hasAnyThreadMessages(uid: string, threadId: string): Promise<boolean> {
  assertCurrentUid(uid, 'hasAnyThreadMessages');
  const ref = threadMessagesRef(uid, threadId);
  const legacySnap = await withBackoff(() => getDoc(ref));
  if (legacySnap.exists()) {
    const data = legacySnap.data() as ChatThreadMessagesDoc | undefined;
    if (Array.isArray(data?.messages) && (data?.messages as unknown[]).length > 0) return true;
  }
  const ownHas = await hasThreadMessageDocs(uid, threadId).catch(() => false);
  if (ownHas) return true;
  // P3c: a participant's own tree can be empty while the SHARED store has the
  // group transcript — greet-on-focus must not treat that as an empty thread.
  // limit(1) probe; false while the flag is off or the read is rules-denied.
  return hasAnyGroupThreadMessages(threadId);
}

export interface ThreadMessagesSnapshot {
  messages: ChatMessage[];
  docExists: boolean;
  fromCache: boolean;
}

/**
 * Live listener on a thread's server transcript (cross-device sync,
 * 2026-07-14). Listens to the legacy whole-array doc — BOTH the client
 * mirror and the server task-completion writer already write it, and every
 * rewrite delivers the full (≤100-message) transcript, which is exactly the
 * shape the existing consumer pipeline handles.
 *
 * Local-echo suppression: this device's own debounced mirror write surfaces
 * first as a hasPendingWrites snapshot — skipped here; the post-ack snapshot
 * carries identical content and dedupes to a no-op in the caller's merge.
 */
export function subscribeThreadMessages(
  uid: string,
  threadId: string,
  cb: (snap: ThreadMessagesSnapshot) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeThreadMessages');
  const ref = threadMessagesRef(uid, threadId);
  // 2026-08-11 (Fernando DR two-store divergence): the listener used to watch
  // ONLY the legacy array doc, so truth that existed solely in the per-doc
  // store (server task completions dual-write it unconditionally) was
  // invisible to every open tab. Listen to BOTH stores and emit the same
  // never-downgrade union the read path uses (mergeLegacyAndPerDoc), so a
  // completed memo reaches live tabs even when the legacy doc is poisoned by
  // a same-anchor Interrupted card.
  let legacyMessages: ChatMessage[] = [];
  let legacyExists = false;
  let legacySeeded = false;
  let perDocMessages: ChatMessage[] = [];
  const emit = (fromCache: boolean): void => {
    cb({
      messages: collapseUserRowTwins(mergeLegacyAndPerDoc(legacyMessages, perDocMessages)),
      docExists: legacyExists || perDocMessages.length > 0,
      fromCache,
    });
  };
  // M11 idiom (chatThreadIndexRepository): an onSnapshot error is TERMINAL for
  // that listener — resubscribe on capped backoff; never leave the ACTIVE
  // thread's transcript deaf for the rest of the session (returning-tab
  // reliability, 2026-09-01). Both legs self-heal independently: the legacy
  // doc leg forwards each error to onError (the hook health-stamps it — it
  // must NOT markSeen, see useChatThreads' fail-closed gate) and the per-doc
  // leg keeps its legacy-only-fallback warn semantics while it retries.
  let disposed = false;
  let unsubLegacy: Unsubscribe | null = null;
  let legacyRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let legacyRetryDelayMs = 2000;
  const attachLegacy = () => {
    if (disposed) return;
    unsubLegacy = onSnapshot(ref, (snap) => {
      // Only a SERVER-confirmed snapshot resets the backoff. Persistence
      // delivers a cache snapshot on every reattach even when the watch
      // stream is about to error again (e.g. revoked read access) — a
      // cache-reset would pin the "capped" backoff at a 2s hot loop.
      if (!snap.metadata.fromCache) legacyRetryDelayMs = 2000;
      if (snap.metadata.hasPendingWrites) return;
      const data = snap.exists() ? (snap.data() as ChatThreadMessagesDoc) : null;
      legacyExists = snap.exists();
      legacyMessages = data ? fromChatThreadMessagesDoc(data) : [];
      legacySeeded = true;
      emit(snap.metadata.fromCache);
    }, (error) => {
      onError?.(error);
      console.error('[chatThreadMessagesRepository] transcript listener errored — resubscribing', {
        threadId,
        delayMs: legacyRetryDelayMs,
        error: error instanceof Error ? error.message : String(error),
      });
      try { unsubLegacy?.(); } catch { /* already torn down */ }
      unsubLegacy = null;
      if (disposed) return;
      legacyRetryTimer = setTimeout(() => {
        legacyRetryTimer = null;
        attachLegacy();
      }, legacyRetryDelayMs);
      legacyRetryDelayMs = Math.min(60_000, Math.round(legacyRetryDelayMs * 2));
    });
  };
  let unsubPerDoc: (() => void) | null = null;
  let perDocRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let perDocRetryDelayMs = 2000;
  const attachPerDoc = () => {
    if (disposed) return;
    unsubPerDoc = subscribeNewestThreadMessageDocs(uid, threadId, (messages, fromCache) => {
      // Server-confirmed snapshots only — same hot-loop guard as the legacy leg.
      if (!fromCache) perDocRetryDelayMs = 2000;
      perDocMessages = messages;
      // Wait for the legacy seed (fires even for a missing doc) so the first
      // emission is never a per-doc-only partial that momentarily hides
      // legacy-only messages from the consumer's merge.
      if (!legacySeeded) return;
      emit(fromCache);
    }, (error) => {
      // Per-doc listener errors degrade to legacy-only behavior (pre-2026-08-11)
      // rather than surfacing an error for a store the thread may not use —
      // but the leg still resubscribes so a recovered stream resumes delivery.
      console.warn('[chatThreadMessagesRepository] per-doc listener error (legacy-only fallback) — resubscribing', error);
      try { unsubPerDoc?.(); } catch { /* already torn down */ }
      unsubPerDoc = null;
      if (disposed) return;
      perDocRetryTimer = setTimeout(() => {
        perDocRetryTimer = null;
        attachPerDoc();
      }, perDocRetryDelayMs);
      perDocRetryDelayMs = Math.min(60_000, Math.round(perDocRetryDelayMs * 2));
    });
  };
  attachLegacy();
  attachPerDoc();
  return () => {
    disposed = true;
    if (legacyRetryTimer) { clearTimeout(legacyRetryTimer); legacyRetryTimer = null; }
    if (perDocRetryTimer) { clearTimeout(perDocRetryTimer); perDocRetryTimer = null; }
    try { unsubLegacy?.(); } catch { /* already torn down */ }
    unsubLegacy = null;
    try { unsubPerDoc?.(); } catch { /* already torn down */ }
    unsubPerDoc = null;
  };
}

export function __resetPendingWritesByUidForTests(): void {
  pendingWritesByUid.clear();
}
